import { exec } from "node:child_process";
import {
  mkdir,
  readFile,
  rename,
  stat,
  unlink,
  writeFile,
} from "node:fs/promises";
import path from "node:path";
import { promisify } from "node:util";
import type {
  DevPortalAuthConfig,
  DevPortalConfig,
  SidebarConfig,
} from "@zuplo/types";
import chalk from "chalk";
import glob from "fast-glob";
import { format } from "prettier";
import { printDiagnosticsToConsole } from "../../../common/output.js";
import { PluginResult, runPlugins } from "../../../common/runner.js";
import { stringifyConfig } from "../../../common/utils/stringify-config.js";
import type {
  Arguments,
  MigrationConfig,
  MigrationData,
  MigrationPlugin,
  ZudokuConfig,
  ZudokuNavigationItem,
} from "./types.js";

const exists = (path: string) =>
  stat(path)
    .then(() => true)
    .catch(() => false);

const deleteUndefinedValues = (obj: Record<string, unknown>) => {
  Object.entries(obj).forEach(([key, value]) => {
    if (value === undefined) {
      delete obj[key];
    }
  });
};

const execAsync = promisify(exec);

export const migrateDevPortal = async (args: Arguments) => {
  const absoluteBaseDir = path.resolve(args.dir);

  const tasks = [
    checkGitStatus,
    loadInputFiles,
    validateDevPortalEnabled,
    convertBasicConfig,
    convertSidebar,
    handleApiReferences,
    handleAuthentication,
    handleAdditionalFeatures,
    updatePackageJsonWorkspaces,
    createPackageJson,
    createTsConfig,
    moveMarkdownFiles,
    writeConfigFile,
    createDevPortalRedirectConfig,
    cleanupOldFiles,
  ];

  const pluginConfig: MigrationConfig = {
    absoluteBaseDir,
    force: args.force,
  };

  const { errors, warnings, notes } = await runPlugins<
    MigrationConfig,
    MigrationData
  >(tasks, pluginConfig);

  // Handle plugin errors first (these are execution failures)
  if (errors.length > 0) {
    printDiagnosticsToConsole("❌ Migration failed with errors:");
    errors.forEach((error) =>
      printDiagnosticsToConsole(`- [${error.pluginName}] ${error.message}`)
    );
    process.exit(1);
  }

  printDiagnosticsToConsole(
    chalk.green("\n✅ Migration completed successfully! 🎉")
  );

  if (notes.length > 0) {
    printDiagnosticsToConsole("\n📝 Migration notes:");
    notes.forEach((note) => printDiagnosticsToConsole(`- ${note}`));
  }

  if (warnings.length > 0) {
    printDiagnosticsToConsole("\n⚠️  Plugin warnings:");
    warnings.forEach((warning) =>
      printDiagnosticsToConsole(`- [${warning.pluginName}] ${warning.message}`)
    );
  }

  printDiagnosticsToConsole("\nNext steps:");
  printDiagnosticsToConsole("1. Switch to the docs directory:");
  printDiagnosticsToConsole(
    `   ${chalk.cyan(`cd ${path.join(absoluteBaseDir, "docs")}`)}`
  );
  printDiagnosticsToConsole("\n2. Install dependencies:");
  printDiagnosticsToConsole(`   ${chalk.cyan("npm install")}`);
  printDiagnosticsToConsole("\n3. Start the dev portal:");
  printDiagnosticsToConsole(`   ${chalk.cyan("npm run dev")}`);
  printDiagnosticsToConsole("");
};

const checkGitStatus: MigrationPlugin = async (config) => {
  if (config.force) return new PluginResult();

  try {
    // Check if we're in a git repository first
    await execAsync("git rev-parse --git-dir", { cwd: config.absoluteBaseDir });

    // Check for uncommitted changes
    const status = await execAsync("git status --porcelain", {
      cwd: config.absoluteBaseDir,
    });
    if (status.stdout.trim() !== "") {
      return new PluginResult({
        pluginName: "check-git-status",
        message:
          "There are uncommitted changes in the repository.\nCommit or stash them before running the migration, or use --force to proceed anyway.",
        severity: "error",
      });
    }
  } catch {
    // If git command fails, likely not a git repository - continue silently
  }
  return new PluginResult();
};

const loadInputFiles: MigrationPlugin = async (config, { data }) => {
  const dir = config.absoluteBaseDir;

  try {
    const packageJson = JSON.parse(
      await readFile(path.join(dir, "package.json"), "utf-8")
    );
    const inputFile = path.join(dir, "config/dev-portal.json");
    const sidebarFile = path.join(dir, "docs/sidebar.json");

    const devPortalConfig: DevPortalConfig = JSON.parse(
      await readFile(inputFile, "utf-8")
    );

    const sidebar: SidebarConfig = JSON.parse(
      await readFile(sidebarFile, "utf-8")
    );

    const themeCssFilePath = path.join(dir, "docs/theme.css");
    if (await exists(themeCssFilePath)) {
      data.themeCss = await readFile(themeCssFilePath, "utf-8");
    }

    data.packageJson = packageJson;
    data.devPortalConfig = devPortalConfig;
    data.sidebar = sidebar;

    return new PluginResult();
  } catch (error) {
    return new PluginResult({
      pluginName: "load-input-files",
      message: `Failed to load input files: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const validateDevPortalEnabled: MigrationPlugin = async (_, { data }) => {
  if (data.devPortalConfig?.enabled === false) {
    return new PluginResult({
      pluginName: "validate-dev-portal-enabled",
      message: "Dev portal is not enabled in the config",
      severity: "error",
    });
  }

  return new PluginResult();
};

const convertBasicConfig: MigrationPlugin = async (_, { data }) => {
  try {
    if (!data.devPortalConfig) {
      return new PluginResult({
        pluginName: "convert-basic-config",
        message: "Dev portal config not found",
        severity: "error",
      });
    }

    const zudokuConfig: ZudokuConfig = {
      site: {
        title: data.devPortalConfig.pageTitle || "Developer Portal",
        logo: {
          src: {
            light: "https://cdn.zuplo.com/assets/my-dev-portal-light.svg",
            dark: "https://cdn.zuplo.com/assets/my-dev-portal-dark.svg",
          },
          alt: data.devPortalConfig.pageTitle || "Developer Portal",
          href: "/",
        },
      },
    };

    // Map favicon
    if (data.devPortalConfig.faviconUrl) {
      zudokuConfig.metadata = {
        ...zudokuConfig.metadata,
        favicon: data.devPortalConfig.faviconUrl,
      };
    }

    // Map page title
    if (data.devPortalConfig.pageTitle) {
      zudokuConfig.metadata = {
        ...zudokuConfig.metadata,
        title: data.devPortalConfig.pageTitle,
      };
    }

    // Map site pathname to basePath
    if (data.devPortalConfig.sitePathname) {
      zudokuConfig.basePath = data.devPortalConfig.sitePathname;
    }

    // Extract logo URL from theme CSS if available
    if (data.themeCss) {
      try {
        const logoUrlMatch = data.themeCss.match(
          /--logo-url\s*:\s*url\((.*?)\)/
        );
        if (logoUrlMatch?.[1]) {
          const logoUrl = logoUrlMatch[1].trim();
          zudokuConfig.site = zudokuConfig.site || {};
          zudokuConfig.site.logo = {
            ...zudokuConfig.site.logo,
            src: {
              light: logoUrl,
              dark: logoUrl,
            },
            alt: zudokuConfig.site.title || "Developer Portal",
            href: "/",
            width: 200,
          };
        }
      } catch (error) {
        // Log warning but don't fail the migration
        const result = new PluginResult();
        result.warnings.push({
          message: `Failed to extract logo URL from theme.css. You will need to set the logo manually.`,
          pluginName: "convert-basic-config",
          severity: "warning",
        });
        data.zudokuConfig = zudokuConfig;
        return result;
      }
    }

    data.zudokuConfig = zudokuConfig;
    return new PluginResult();
  } catch (error) {
    return new PluginResult({
      pluginName: "convert-basic-config",
      message: `Failed to convert basic config: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const convertSidebar: MigrationPlugin = async (_, { data }) => {
  try {
    if (!data.sidebar || !data.zudokuConfig) {
      return new PluginResult({
        pluginName: "convert-sidebar",
        message: "Required data not found",
        severity: "error",
      });
    }

    const convertSidebarToNavigation = (
      oldSidebar: SidebarConfig
    ): ZudokuNavigationItem[] => {
      const navigation: ZudokuNavigationItem[] = [];

      if (!oldSidebar.docs) {
        return navigation;
      }

      for (const item of oldSidebar.docs) {
        if (typeof item === "string") {
          navigation.push(item);
        } else if (item.type === "doc") {
          const docItem: ZudokuNavigationItem = {
            type: "doc",
            file: item.id,
            label: item.label,
          };

          if ("defaultPage" in item && item.defaultPage) {
            docItem.path = "/";
          }

          navigation.push(docItem);
        } else if (item.type === "category") {
          const category: ZudokuNavigationItem = {
            type: "category",
            label: item.label,
            items: [],
          };

          if (item.link) {
            if (typeof item.link === "string") {
              category.link = item.link;
            } else if (item.link.type === "doc") {
              category.link = { type: "doc", file: item.link.id };
            }
          }

          if (item.items) {
            category.items = [];
            for (const subItem of item.items) {
              if (typeof subItem === "string") {
                category.items.push({
                  type: "doc",
                  file: subItem,
                });
              } else if (subItem.type === "doc") {
                category.items.push({
                  type: "doc",
                  file: subItem.id,
                  label: subItem.label,
                });
              }
            }
          }

          navigation.push(category);
        }
      }

      return navigation;
    };

    const navigation = convertSidebarToNavigation(data.sidebar);
    data.zudokuConfig = {
      ...data.zudokuConfig,
      navigation,
    };

    const result = new PluginResult();
    if (navigation.length > 0) {
      result.notes.push(
        `Converted sidebar navigation: ${navigation.length} navigation items`
      );
    }

    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "convert-sidebar",
      message: `Failed to convert sidebar: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const handleApiReferences: MigrationPlugin = async (_, { data }) => {
  try {
    if (!data.sidebar || !data.zudokuConfig) {
      return new PluginResult({
        pluginName: "handle-api-references",
        message: "Required data not found",
        severity: "error",
      });
    }

    const apiRefItems = data.sidebar.docs?.filter(
      (item) => typeof item !== "string" && item.type === "api-ref"
    );

    const result = new PluginResult();

    if (!apiRefItems || apiRefItems.length === 0) {
      return result;
    }

    data.zudokuConfig.apis = apiRefItems.map((apiRefItem, index) => {
      const specInput = apiRefItem.defaultSpec
        ? `../config/${apiRefItem.defaultSpec}.oas.json`
        : "../config/routes.oas.json";

      const apiPath = index === 0 ? "/api" : `/api-${index + 1}`;

      result.notes.push(`Migrated API reference to ${apiPath}`);

      return {
        type: "file" as const,
        input: specInput,
        path: apiPath,
      };
    });

    if (apiRefItems.length > 1) {
      result.warnings.push({
        message:
          "Multiple API references found. Adding multiple links to the navigation. For advanced use cases you can use an API catalog. See: https://zuplo.com/docs/dev-portal/zudoku/configuration/api-catalog.",
        pluginName: "handle-api-references",
        severity: "warning",
      });
    }

    const navigationLinks = apiRefItems.map((apiRefItem, index) => {
      const apiPath = index === 0 ? "/api" : `/api-${index + 1}`;
      return {
        type: "link" as const,
        to: apiPath,
        label: apiRefItem.label,
      };
    });

    // Add API navigation links to the existing navigation
    data.zudokuConfig.navigation = [
      ...(data.zudokuConfig.navigation || []),
      ...navigationLinks,
    ];

    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "handle-api-references",
      message: `Failed to handle API references: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const handleAuthentication: MigrationPlugin = async (_, { data }) => {
  try {
    if (!data.devPortalConfig || !data.zudokuConfig) {
      return new PluginResult({
        pluginName: "handle-authentication",
        message: "Required data not found",
        severity: "error",
      });
    }

    if (data.devPortalConfig.authentication) {
      const auth = data.devPortalConfig.authentication as DevPortalAuthConfig;

      // Extract client info - handle both current and legacy structures
      let clientId: string | undefined;
      let audience: string | undefined;
      let issuer: string | undefined;
      let scope: string | undefined;

      if ("clientId" in auth) {
        // Current format
        clientId = auth.clientId;
        audience = auth.audience;
        issuer = auth.issuer;
        scope = auth.scope;
      } else if ("devPortalClient" in auth) {
        // Legacy format
        clientId = auth.devPortalClient?.clientId;
        audience = auth.devPortalClient?.audience;
        issuer = auth.authority;
      }

      if (auth.provider === "auth0") {
        data.zudokuConfig.authentication = {
          type: "auth0",
          clientId,
          domain: issuer?.replace("https://", "").replace("/", ""),
          audience,
          scopes: scope?.split(" "),
        };
      } else if (auth.provider === "clerk") {
        const clerkPubKey = issuer?.match(/clerk\.accounts\.dev/)?.[0]
          ? issuer.split("://")[1]?.split(".")[0]
          : undefined;

        data.zudokuConfig.authentication = {
          type: "clerk",
          clerkPubKey: clerkPubKey || clientId,
        };
      } else {
        // All other providers (azure-ad, okta, oidc, etc.) use OpenID Connect
        data.zudokuConfig.authentication = {
          type: "openid",
          clientId,
          issuer,
          audience,
          scopes: scope?.split(" "),
        };
      }
    }

    if (data.zudokuConfig.authentication) {
      deleteUndefinedValues(data.zudokuConfig.authentication);
    }

    const result = new PluginResult();
    if (data.zudokuConfig.authentication) {
      result.notes.push(
        `Configured ${data.zudokuConfig.authentication.type} authentication`
      );
    }

    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "handle-authentication",
      message: `Failed to handle authentication: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const handleAdditionalFeatures: MigrationPlugin = async (_, { data }) => {
  try {
    if (!data.devPortalConfig || !data.sidebar || !data.zudokuConfig) {
      return new PluginResult({
        pluginName: "handle-additional-features",
        message: "Required data not found",
        severity: "error",
      });
    }

    const result = new PluginResult();

    // Handle API keys
    if (data.devPortalConfig.apiKeys) {
      data.zudokuConfig.apiKeys = {
        enabled: true,
      };
    }

    // Check for unsupported specs filtering
    const hasSpecsFiltering = data.sidebar.docs?.some((item) => {
      if (typeof item === "string") return false;
      if (item.type === "doc" && "specs" in item && item.specs) return true;
      if (item.type === "category" && "specs" in item && item.specs)
        return true;
      if (item.type === "category" && item.items) {
        return item.items.some(
          (subItem) =>
            typeof subItem !== "string" && "specs" in subItem && subItem.specs
        );
      }
      return false;
    });

    if (hasSpecsFiltering) {
      result.warnings.push({
        message:
          "The 'specs' property for filtering docs by OpenAPI specification is not supported in Zudoku",
        pluginName: "handle-additional-features",
        severity: "warning",
      });
    }

    if (data.devPortalConfig.monetization) {
      result.warnings.push({
        message:
          "Monetization configuration is not supported in the new dev portal.",
        pluginName: "handle-additional-features",
        severity: "warning",
      });
    }
    if (data.devPortalConfig.enableMetering) {
      result.warnings.push({
        message: "Metering is not supported in the new dev portal.",
        pluginName: "handle-additional-features",
        severity: "warning",
      });
    }
    if (
      data.devPortalConfig.authorizePathname &&
      data.devPortalConfig.authorizePathname !== "/oauth/authorize"
    ) {
      result.warnings.push({
        message:
          "Custom authorize pathname is not configurable in the new dev portal.",
        pluginName: "handle-additional-features",
        severity: "warning",
      });
    }

    // Add notes for successfully configured features
    if (data.zudokuConfig.apiKeys?.enabled) {
      result.notes.push("Enabled API keys support");
    }

    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "handle-additional-features",
      message: `Failed to handle additional features: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const createPackageJson: MigrationPlugin = async (config) => {
  try {
    const packageJsonFile = path.join(
      config.absoluteBaseDir,
      "docs/package.json"
    );

    if (!config.force && (await exists(packageJsonFile))) {
      return new PluginResult({
        pluginName: "create-package-json",
        message: "package.json already exists",
        severity: "warning",
      });
    }

    // get the version of zudoku from npm online:
    const zudokuPackageJson = await fetch(
      "https://cdn.jsdelivr.net/npm/zudoku/package.json"
    ).then((res) => res.json());

    const packageJsonContent = {
      name: "docs",
      version: "1.0.0",
      type: "module",
      private: true,
      scripts: {
        dev: "zudoku dev --zuplo",
        build: "zudoku build",
        start: "zudoku start",
      },
      dependencies: {
        zudoku: zudokuPackageJson.version || "latest",
        react: zudokuPackageJson.dependencies.react || "^19.0.0",
        "react-dom": zudokuPackageJson.dependencies["react-dom"] || "^19.0.0",
      },
      devDependencies: {
        "@types/node": "^22",
        "@types/react": ">=19",
        "@types/react-dom": ">=19",
        typescript: zudokuPackageJson.devDependencies.typescript || "^5",
      },
    };

    await writeFile(
      packageJsonFile,
      JSON.stringify(packageJsonContent, null, 2)
    );

    const result = new PluginResult();
    result.notes.push("Wrote docs/package.json");
    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "create-package-json",
      message: `Failed to create package.json: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const createTsConfig: MigrationPlugin = async (config) => {
  try {
    const tsConfigFile = path.join(
      config.absoluteBaseDir,
      "docs/tsconfig.json"
    );

    if (!config.force && (await exists(tsConfigFile))) {
      return new PluginResult({
        pluginName: "create-tsconfig",
        message: "tsconfig.json already exists",
        severity: "warning",
      });
    }

    const tsConfigContent = await fetch(
      "https://cdn.jsdelivr.net/npm/create-zudoku/dist/templates/zuplo/tsconfig.json"
    ).then((res) => res.text());

    await writeFile(tsConfigFile, tsConfigContent);

    const result = new PluginResult();
    result.notes.push("Wrote docs/tsconfig.json");
    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "create-tsconfig",
      message: `Failed to create tsconfig.json: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const updatePackageJsonWorkspaces: MigrationPlugin = async (config) => {
  try {
    const packageJsonFile = path.join(config.absoluteBaseDir, "package.json");

    if (!(await exists(packageJsonFile))) {
      return new PluginResult({
        pluginName: "update-package-json-workspaces",
        message: "Root package.json not found",
        severity: "warning",
      });
    }

    const packageJsonContent = JSON.parse(
      await readFile(packageJsonFile, "utf-8")
    );

    if (Array.isArray(packageJsonContent.workspaces)) {
      if (!packageJsonContent.workspaces.includes("docs")) {
        packageJsonContent.workspaces.push("docs");
      }
    } else if (!packageJsonContent.workspaces) {
      packageJsonContent.workspaces = ["docs"];
    }

    packageJsonContent.scripts = packageJsonContent.scripts || {};
    if (!packageJsonContent.scripts["docs"]) {
      packageJsonContent.scripts["docs"] = "npm run dev --workspace docs";
    }

    await writeFile(
      packageJsonFile,
      JSON.stringify(packageJsonContent, null, 2)
    );

    const result = new PluginResult();
    result.notes.push("Updated package.json workspaces");

    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "update-package-json-workspaces",
      message: `Failed to update package.json workspaces: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const moveMarkdownFiles: MigrationPlugin = async (config) => {
  try {
    const docsDir = path.join(config.absoluteBaseDir, "docs");
    const pagesDir = path.join(docsDir, "pages");

    const markdownFiles = await glob(["*.md", "*.mdx"], {
      cwd: docsDir,
      absolute: false,
    });

    try {
      await mkdir(pagesDir, { recursive: true });
    } catch {
      // directory might already exist, that's okay
    }

    const result = new PluginResult();
    const movedFiles: string[] = [];

    for (const file of markdownFiles) {
      const sourcePath = path.join(docsDir, file);
      const targetPath = path.join(pagesDir, file);

      try {
        await rename(sourcePath, targetPath);
        movedFiles.push(file);
      } catch (error) {
        result.warnings.push({
          message: `Failed to move ${file}: ${(error as Error).message}`,
          pluginName: "move-markdown-files",
          severity: "warning",
        });
      }
    }

    if (movedFiles.length > 0) {
      result.notes.push(
        `Moved ${movedFiles.length} markdown files to docs/pages/`
      );
    }

    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "move-markdown-files",
      message: `Failed to move markdown files: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const createDevPortalRedirectConfig: MigrationPlugin = async (config) => {
  try {
    const devPortalOasFile = path.join(
      config.absoluteBaseDir,
      "config",
      "dev-portal.oas.json"
    );

    const oasContent = {
      openapi: "3.1.0",
      info: {
        version: "1.0.0",
        title: "Dev Portal Redirect",
      },
      paths: {
        "/docs(.*)": {
          "x-zuplo-path": {
            pathMode: "url-pattern",
          },
          get: {
            summary: "Redirect",
            description: "Redirect to the new Dev Portal domain",
            "x-zuplo-route": {
              corsPolicy: "none",
              handler: {
                export: "legacyDevPortalHandler",
                module: "$import(@zuplo/runtime)",
                options: {
                  mode: "redirect",
                },
              },
              policies: {
                inbound: [],
              },
            },
            operationId: "dev-portal-redirect",
            "x-internal": true,
          },
        },
      },
    };

    await writeFile(devPortalOasFile, JSON.stringify(oasContent, null, 2));

    const result = new PluginResult();
    result.notes.push(
      "Created config/dev-portal.oas.json for legacy dev portal redirects"
    );
    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "create-dev-portal-redirect-config",
      message: `Failed to create dev-portal.oas.json: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const cleanupOldFiles: MigrationPlugin = async (config) => {
  try {
    const filesToCleanup = [
      path.join(config.absoluteBaseDir, "config/dev-portal.json"),
      path.join(config.absoluteBaseDir, "docs/sidebar.json"),
      path.join(config.absoluteBaseDir, "docs/theme.css"),
    ];

    const result = new PluginResult();
    const cleanedFiles: string[] = [];

    for (const filePath of filesToCleanup) {
      try {
        if (await exists(filePath)) {
          await unlink(filePath);
          cleanedFiles.push(path.relative(config.absoluteBaseDir, filePath));
        }
      } catch (error) {
        result.warnings.push({
          message: `Failed to remove ${path.relative(config.absoluteBaseDir, filePath)}: ${(error as Error).message}`,
          pluginName: "cleanup-old-files",
          severity: "warning",
        });
      }
    }

    if (cleanedFiles.length > 0) {
      result.notes.push(`Cleaned up old files: ${cleanedFiles.join(", ")}`);
    }

    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "cleanup-old-files",
      message: `Failed to cleanup old files: ${(error as Error).message}`,
      severity: "error",
    });
  }
};

const writeConfigFile: MigrationPlugin = async (config, { data }) => {
  try {
    if (!data.zudokuConfig) {
      return new PluginResult({
        pluginName: "write-output-file",
        message: "Zudoku config not found",
        severity: "error",
      });
    }

    const outputFile = path.join(
      config.absoluteBaseDir,
      "docs/zudoku.config.tsx"
    );

    const configContent = [];

    configContent.push(`// This file was generated by the Zuplo CLI migrate-dev-portal command.
// It is used to configure the Zuplo dev portal.
// You can edit this file to customize the dev portal.
// For more information, see https://zuplo.com/docs/dev-portal/zudoku/configuration/overview`);

    configContent.push(`import type { ZudokuConfig } from "zudoku";\n`);
    configContent.push(
      `const config: ZudokuConfig = ${stringifyConfig(data.zudokuConfig)}`
    );
    configContent.push(`export default config;`);

    await writeFile(
      outputFile,
      await format(configContent.join("\n"), { parser: "typescript" })
    );

    const result = new PluginResult();
    result.notes.push("Wrote docs/zudoku.config.tsx");
    return result;
  } catch (error) {
    return new PluginResult({
      pluginName: "write-output-file",
      message: `Failed to write output file: ${(error as Error).message}`,
      severity: "error",
    });
  }
};
